import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const API = "https://api.m3campus.com";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me/`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));

      if (data && data.authenticated) {
        setUser({
          username: data.username || "",
          email: data.email || "",
          nombre: data.nombre || "",
        });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const getCsrfToken = useCallback(async () => {
    const res = await fetch(`${API}/csrf/`, { credentials: "include" });
    if (!res.ok) throw new Error("No se pudo obtener CSRF");
    const data = await res.json();
    return data.csrfToken;
  }, []);

  const login = useCallback(async (email, password) => {
    const csrfToken = await getCsrfToken();

    const res = await fetch(`${API}/auth/login/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify({ email, password }),
    });

    const text = await res.text();
    let data = {};
    try { data = JSON.parse(text); } catch {}

    if (!res.ok) {
      throw new Error(data.detail || text || "Error en login");
    }

    await refresh();
    return true;
  }, [getCsrfToken, refresh]);

  const logout = useCallback(async () => {
    const csrfToken = await getCsrfToken();

    const res = await fetch(`${API}/auth/logout/`, {
      method: "POST",
      credentials: "include",
      headers: { "X-CSRFToken": csrfToken },
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || "Error en logout");
    }

    setUser(null);
  }, [getCsrfToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ loading, user, isAuthenticated: !!user, refresh, login, logout }),
    [loading, user, refresh, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}